package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Customer
import com.solevo.dist.application.models.Quote
import com.solevo.dist.application.models.QuoteStatus
import com.solevo.dist.application.models.AnswerQuoteRequest
import com.solevo.dist.application.models.User
import com.solevo.dist.repository.ApplicationQuoteRepository
import com.solevo.dist.repository.ApplicationCustomerRepository
import com.solevo.dist.network.firebase.api.user.UserFirebaseRemoteSource
import com.solevo.dist.i18n.Localization
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.StatusMapper
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import org.koin.mp.KoinPlatform.getKoin

class QuotesViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val quoteRepository: ApplicationQuoteRepository = getKoin().get()
    private val userRemoteSource: UserFirebaseRemoteSource = getKoin().get()
    private val customerRepository: ApplicationCustomerRepository = getKoin().get()

    private val _state = MutableStateFlow(QuotesUiState())
    val state: StateFlow<QuotesUiState> = _state.asStateFlow()

    private var allQuotes: List<Quote> = emptyList()
    private var currentCustomer: Customer? = null

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        if (_state.value.loading.not() && allQuotes.isNotEmpty()) return
        _state.value = _state.value.copy(loading = true, error = null)

        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    if (user.customerId.isNotEmpty()) {
                        customerRepository.getCurrentCustomer()
                            .combine(quoteRepository.getQuotesLive()) { customer, quotes ->
                                Pair(customer, quotes)
                            }
                    } else {
                        flowOf(Pair(null, emptyList<Quote>()))
                    }
                }
                .onEach { (customer, quotes) ->
                    currentCustomer = customer
                    allQuotes = quotes.sortedWith(
                        compareBy<Quote> { it.createdAt }.thenBy { it.quoteId }
                    ).asReversed()
                    computeAndEmit()
                }
                .catch { t ->
                    _state.value = _state.value.copy(
                        loading = false,
                        error = t.message ?: "Unknown error",
                        rows = emptyList()
                    )
                }
                .collect()
        }
    }

    fun stop() = scope.cancel()

    fun selectTab(tab: QuotesTab) {
        if (_state.value.selectedTab == tab) return
        _state.value = _state.value.copy(selectedTab = tab)
        computeAndEmit()
    }

    fun cancelQuoteViaHttp(
        quoteId: String,
        onDone: (() -> Unit)? = null,
        onError: ((Throwable) -> Unit)? = null
    ) {
        scope.launch {
            val req = AnswerQuoteRequest(quoteId = quoteId, legalEntityId = User.legalId, customerId = User.currentCustomerId.value)
            val result = quoteRepository.cancelQuote(req)
            result.onSuccess {
                // Optimistically remove from local cache and update UI
                allQuotes = allQuotes.filterNotNull().filter { it.quoteId != quoteId }
                computeAndEmit()
                onDone?.invoke()
            }.onFailure { t ->
                _state.value = _state.value.copy(error = t.message ?: "Failed to cancel quote")
                onError?.invoke(t)
            }
        }
    }

    fun clearError() {
        _state.value = _state.value.copy(error = null)
    }

    // fun deleteQuote(quoteId: String, onDone: (() -> Unit)? = null, onError: ((Throwable) -> Unit)? = null) {
    //     scope.launch {
    //         quotesRemoteSource.deleteQuote(quoteId)
    //             .onSuccess {
    //                 onDone?.invoke()
    //             }
    //             .onFailure { t ->
    //                 _state.value = _state.value.copy(error = t.message ?: "Failed to delete quote")
    //                 onError?.invoke(t)
    //             }
    //     }
    // }

    private fun computeAndEmit() {
        val tab = _state.value.selectedTab
        val filtered = when (tab) {
            QuotesTab.All -> allQuotes
            QuotesTab.Awaiting -> allQuotes.filter { it.status == QuoteStatus.Awaiting }
            QuotesTab.Received -> allQuotes.filter { it.status == QuoteStatus.Received }
        }

        val rows = filtered.map { it.toRow() }

        _state.value = _state.value.copy(
            loading = false,
            error = null,
            rows = rows
        )
    }

    /* ---------------- UI mapping helpers ---------------- */

    private fun Quote.toRow(): QuoteRowUi {
        val strings = Localization.strings.value
        val statusInfo = StatusMapper.mapQuoteStatus(status)
        val localizedStatusText = when (status) {
            QuoteStatus.Awaiting -> strings.quotesStatusAwaiting
            QuoteStatus.Received -> strings.quotesStatusReceived
            QuoteStatus.Error -> strings.quotesStatusError
            QuoteStatus.Other -> "Other"
        }
        val currency = currentCustomer?.currency ?: "XOF"
        return QuoteRowUi(
            id = quoteId,
            customerReferenceText = if (!orderReference.isNullOrBlank()) orderReference!! else "—",
            quoteIdText = quoteId,
            amountText = AmountFormatter.formatAmountCompact(totalAmount ?: 0.0, currency),
            statusText = localizedStatusText,
            statusColorHex = statusInfo.colorHex,
            description = if (!orderReference.isNullOrBlank()) orderReference!! else "Quote #$quoteId",
            pickupDateText = DateFormatter.formatDateOrDash(pickupDateTo),
            createdDateText = DateFormatter.formatDateOrDash(createdAt)
        )
    }
}

/* ------------ UI state / models ------------ */

enum class QuotesTab(val label: String) { All("All"), Awaiting("Awaiting Quote"), Received("Quote received") }

data class QuoteRowUi(
    val id: String,
    val customerReferenceText: String,
    val quoteIdText: String,
    val amountText: String,
    val statusText: String,
    val statusColorHex: String,
    val description: String,
    val pickupDateText: String,
    val createdDateText: String
)

data class QuotesUiState(
    val loading: Boolean = true,
    val rows: List<QuoteRowUi> = emptyList(),
    val selectedTab: QuotesTab = QuotesTab.All,
    val error: String? = null
)


